import { useState, type MouseEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/lib/AuthContext";
import { ProfileDropdown } from "./ProfileDropdown";
import { TaxonomyModal } from "./TaxonomyModal";
import { AnnouncementsModal } from "./AnnouncementsModal";
import navbarLogo from "@/assets/famibelleza_navbar_logo.png";

export function Navbar() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openTaxonomy, setOpenTaxonomy] = useState<"categories" | "brands" | "announcements" | null>(null);

  const onLogout = async () => {
    setIsMobileMenuOpen(false);
    await logout();
    navigate("/");
  };

  const openCategories = () => {
    setIsMobileMenuOpen(false);
    setOpenTaxonomy("categories");
  };

  const openBrands = () => {
    setIsMobileMenuOpen(false);
    setOpenTaxonomy("brands");
  };

  const openAnnouncements = () => {
    setIsMobileMenuOpen(false);
    setOpenTaxonomy("announcements");
  };

  const onProductsClick = (event: MouseEvent<HTMLAnchorElement>) => {
    setIsMobileMenuOpen(false);
    if (location.pathname !== "/products" || location.search) return;

    event.preventDefault();
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <nav className="fixed w-[95%] md:w-[80%] z-20 top-0 left-1/2 -translate-x-1/2 mt-4 backdrop-blur-md bg-app-navbar px-2 border border-app-border shadow-lg max-w-[1160px] rounded-4xl transition-all duration-300">
      <div className="max-w-screen-xl flex flex-wrap items-center justify-between p-2">
        <Link to="/" className="flex items-center gap-3 hover:scale-105 transition-transform duration-300">
          <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-white p-1 shadow-sm">
            <img
              alt={t("common.famibelleza")}
              src={navbarLogo}
              width={61}
              height={64}
              loading="eager"
              fetchPriority="high"
              className="h-8 w-auto"
            />
          </span>
          <span className="self-center text-xl hidden sm:block sm:text-2xl font-semibold whitespace-nowrap text-foreground">
            {t("common.famibelleza")}
          </span>
        </Link>

        <div className="flex md:order-2 items-center gap-2">
          <div className="hidden lg:block">
            {user ? (
              <ProfileDropdown
                user={user}
                onLogout={onLogout}
                onOpenCategories={openCategories}
                onOpenBrands={openBrands}
                onOpenAnnouncements={openAnnouncements}
              />
            ) : (
              <div className="flex items-center gap-2">
                <Link to="/login" className="rounded-full px-4 py-1.5 font-medium text-white bg-brand hover:bg-brand/90 transition-colors duration-200">
                  {t("nav.logIn")}
                </Link>
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={() => setIsMobileMenuOpen(prev => !prev)}
            className="inline-flex lg:hidden items-center p-2 justify-center text-app-muted rounded-lg hover:bg-app-surface-hover hover:text-foreground cursor-pointer"
            aria-expanded={isMobileMenuOpen}
            aria-label={t("nav.openMenu")}
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 17 14">
              <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M1 1h15M1 7h15M1 13h15" />
            </svg>
          </button>
        </div>

        <div className="hidden lg:flex items-center absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          <ul className="flex flex-col p-4 md:p-0 mt-4 font-medium md:space-x-8 md:flex-row md:mt-0">
            <li>
              <Link
                to="/products"
                onClick={onProductsClick}
                className="text-foreground font-bold text-lg hover:text-brand transition-colors duration-200"
              >
                {t("nav.products")}
              </Link>
            </li>
          </ul>
        </div>
      </div>

      <div
        className={`lg:hidden overflow-hidden transition-all duration-300 ease-in-out ${
          isMobileMenuOpen ? "max-h-[400px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="px-4 pb-4 pt-2">
          <ul className="flex flex-col space-y-1">
            <li>
              <Link
                to="/products"
                onClick={onProductsClick}
                className="block py-2.5 px-3 text-foreground font-semibold text-base hover:bg-app-surface-hover rounded-lg transition-colors duration-200"
              >
                {t("nav.products")}
              </Link>
            </li>
          </ul>

          <div className="mt-3 pt-3 border-t border-app-border">
            {user ? (
              <div className="flex flex-col space-y-1">
                <div className="px-3 py-1 text-sm text-app-muted truncate">
                  {user.email}
                  {user.is_superuser && ` (${t("nav.admin")})`}
                </div>
                {user.is_superuser && (
                  <>
                    <Link
                      to="/products/new"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="text-left py-2.5 px-3 font-semibold text-base rounded-lg text-foreground hover:bg-app-surface-hover transition-colors duration-200"
                    >
                      {t("nav.addProduct")}
                    </Link>
                    <button
                      type="button"
                      onClick={openCategories}
                      className="text-left py-2.5 px-3 font-semibold text-base rounded-lg text-foreground hover:bg-app-surface-hover transition-colors duration-200 cursor-pointer"
                    >
                      {t("nav.categories")}
                    </button>
                    <button
                      type="button"
                      onClick={openBrands}
                      className="text-left py-2.5 px-3 font-semibold text-base rounded-lg text-foreground hover:bg-app-surface-hover transition-colors duration-200 cursor-pointer"
                    >
                      {t("nav.brands")}
                    </button>
                    <button
                      type="button"
                      onClick={openAnnouncements}
                      className="text-left py-2.5 px-3 font-semibold text-base rounded-lg text-foreground hover:bg-app-surface-hover transition-colors duration-200 cursor-pointer"
                    >
                      {t("nav.announcements")}
                    </button>
                  </>
                )}
                <button
                  type="button"
                  onClick={onLogout}
                  className="text-left py-2.5 px-3 font-semibold text-base rounded-lg text-red-600 hover:bg-red-50 transition-colors duration-200 cursor-pointer"
                >
                  {t("nav.logOut")}
                </button>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                <Link
                  to="/login"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-center rounded-full px-4 py-2 font-medium text-white bg-brand hover:bg-brand/90 transition-colors duration-200"
                >
                  {t("nav.logIn")}
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      {openTaxonomy === "categories" && (
        <TaxonomyModal
          title={t("taxonomy.categoriesTitle")}
          kind="category"
          onClose={() => setOpenTaxonomy(null)}
          listPath="/categories"
          createPath="/category"
          itemPath={id => `/category/${id}`}
          toPayload={name => ({ name, name_en: name })}
          emptyLabel={t("taxonomy.noCategories")}
          addPlaceholder={t("taxonomy.addCategory")}
        />
      )}

      {openTaxonomy === "brands" && (
        <TaxonomyModal
          title={t("taxonomy.brandsTitle")}
          kind="brand"
          onClose={() => setOpenTaxonomy(null)}
          listPath="/brands"
          createPath="/brand"
          itemPath={id => `/brand/${id}`}
          toPayload={name => ({ name })}
          emptyLabel={t("taxonomy.noBrands")}
          addPlaceholder={t("taxonomy.addBrand")}
        />
      )}

      {openTaxonomy === "announcements" && (
        <AnnouncementsModal onClose={() => setOpenTaxonomy(null)} />
      )}
    </nav>
  );
}
